import { getIn, setIn } from 'formik';
import { useEffect, useState } from 'react';
import { useIntlBds } from '../../../../translations/BdsIntlProvider';
import { DataTableTypes } from '../../DataTableTypes';
import { dataTableClassnames } from '../const/classNames';
import { useDataTableContext } from '../context/DataTableContext';
import { isEmpty, sanitizeObject } from './sanitizeObject';
import { filterNotEmpty } from '../../../../utils/listHelpers';

export function useFilterState() {
  const {
    actions,
    slotProps,
    state,
    model: { query: queryModel, page, sort }
  } = useDataTableContext();
  const { intl } = useIntlBds();

  const searchFilterDefinition = slotProps?.toolbar?.search
    ? ({
        id: 'search',
        label: intl.DataTable.SearchInput.label,
        placeholder: intl.DataTable.SearchInput.placeholder,
        type: DataTableTypes.Filters.Type.input,
        startIcon: 'search'
      } as DataTableTypes.Filters.FilterDefinition<DataTableTypes.QueryModel>)
    : undefined;

  const [localQueryModel, setLocalQueryModel] = useState(queryModel);

  useEffect(() => {
    setLocalQueryModel(queryModel);
  }, [queryModel]);

  const setQueryModel = (key: string, value: unknown) => {
    const nextQueryModel = sanitizeObject(setIn(queryModel, key, value));
    setLocalQueryModel(nextQueryModel);
    actions.setModelChange({
      query: nextQueryModel,
      page,
      sort
    });
  };
  const availableFilterDefinitions = filterNotEmpty([
    searchFilterDefinition,
    ...(slotProps?.filter?.elements({ queryModel: localQueryModel, setQueryModel }) ?? EMPTY_ARRAY)
  ]);

  const activeFilters: { id: string; label?: string; value: unknown }[] = availableFilterDefinitions
    .filter((filter) => !isEmpty(getIn(queryModel, filter.id)))
    .map((filter) => ({
      id: filter.id,
      value: getIn(queryModel, filter.id),
      label: filter.label
    }));

  return {
    count: {
      available: availableFilterDefinitions.length,
      active: activeFilters.length
    },
    drawer: {
      open: state.filterDrawerOpen,
      setOpen: actions.setFilterDrawerOpen
    },
    getQueryValueById: getIn,
    queryModel: localQueryModel,
    setQueryModel,
    clearQueryModel() {
      setLocalQueryModel({});
      actions.setModelChange({
        query: {},
        page,
        sort
      });
    },
    focusSearchInput() {
      const element = document.querySelector(`#${dataTableClassnames.header.searchInput}`) as HTMLInputElement;
      if (element) {
        element.focus();
      }
    },
    clearQueryModelByKey(key: string) {
      const queryModelCopy = sanitizeObject(setIn(localQueryModel, key, undefined));

      setLocalQueryModel(queryModelCopy);
      actions.setModelChange({
        query: queryModelCopy,
        page,
        sort
      });
    },
    availableFilterDefinitions,
    activeFilters
  };
}

const EMPTY_ARRAY: DataTableTypes.Filters.FilterDefinition<DataTableTypes.QueryModel>[] = [];
